import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { ListSkeleton } from "@/components/PageSkeleton";
import {
  listServiceRequests,
  quoteServiceRequest,
  declineServiceRequest,
  completeServiceRequest,
} from "@/lib/services.functions";
import { SERVICE_COMMISSION_RATE } from "@/lib/service-categories";
import { Banknote, Calendar, Check, Inbox, Mail, Send, X } from "lucide-react";
import { useHydratedTranslation } from "@/components/marketing/Layout";
import { toast } from "sonner";
import { buildSeo } from "@/lib/seo";

const REQ_STATUS_KEY: Record<string, string> = {
  all: "stAll",
  new: "stNew",
  quoted: "stQuoted",
  paid: "stPaid",
  completed: "stCompleted",
  declined: "stDeclined",
};

export const Route = createFileRoute("/_authenticated/requests")({
  head: () =>
    buildSeo({
      path: "/requests",
      title: "Service Requests — Reality Twin",
      description:
        "Incoming service requests from the public directory — quote, get paid online and schedule the job.",
      noindex: true,
    }),
  component: Page,
});

const STATUS_HUE: Record<string, string> = {
  new: "60",
  quoted: "240",
  paid: "168",
  completed: "290",
  declined: "20",
};

function Page() {
  const t = useHydratedTranslation();
  const rst = (s: string) => t(`app.pages.requests.${REQ_STATUS_KEY[s] ?? "stNew"}`);
  const fetchList = useServerFn(listServiceRequests);
  const quote = useServerFn(quoteServiceRequest);
  const decline = useServerFn(declineServiceRequest);
  const complete = useServerFn(completeServiceRequest);

  const [data, setData] = useState<any>(null);
  const [filter, setFilter] = useState("all");
  const [quoting, setQuoting] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<"EUR" | "CHF" | "USD">("EUR");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      setData(await fetchList());
    } catch (e: any) {
      toast.error(e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);

  const requests = (data?.requests ?? []).filter(
    (r: any) => filter === "all" || r.status === filter,
  );

  async function sendQuote(id: string) {
    const value = Number(amount);
    if (!value || value <= 0) return toast.error(t("app.pages.requests.enterPrice"));
    setBusy(true);
    try {
      await quote({ data: { id, amount: value, currency, note: note.trim() || undefined } });
      toast.success(t("app.pages.requests.quoteSent"));
      setQuoting(null);
      setAmount("");
      setNote("");
      await load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <DashboardShell
      title={t("app.pages.requests.title")}
      subtitle={t("app.pages.requests.subtitle")}
    >
      {data && !data.offersServices && (
        <div className="glass-strong rounded-2xl p-4 mb-4 flex flex-wrap items-center gap-3 border border-[oklch(0.82_0.18_168_/_0.35)]">
          <span className="text-sm">{t("app.pages.requests.notListed")}</span>
          <Link
            to="/settings"
            className="rounded-full brand-gradient text-black px-4 py-1.5 text-xs font-medium"
          >
            {t("app.pages.requests.enableSettings")}
          </Link>
        </div>
      )}

      <div className="mb-4 flex gap-2 flex-wrap">
        {["all", "new", "quoted", "paid", "completed", "declined"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "brand-gradient text-black border-transparent" : "glass border-border text-muted-foreground hover:bg-white/5"}`}
          >
            {rst(s)}
          </button>
        ))}
      </div>

      {!data ? (
        <ListSkeleton rows={4} />
      ) : requests.length === 0 ? (
        <div className="glass-strong rounded-2xl p-12 text-center">
          <Inbox className="h-8 w-8 mx-auto text-[oklch(0.82_0.18_168)] mb-3" />
          <div className="font-display text-lg font-semibold">
            {t("app.pages.requests.emptyTitle")}
          </div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            {t("app.pages.requests.emptyBody")}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r: any) => {
            const payout =
              r.quoted_amount != null
                ? Number(r.quoted_amount) - Number(r.commission_amount ?? 0)
                : null;
            return (
              <div key={r.id} className="glass-strong rounded-2xl p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-md glass"
                        style={{ color: `oklch(0.82 0.15 ${STATUS_HUE[r.status] ?? "168"})` }}
                      >
                        {rst(r.status)}
                      </span>
                      {r.category && (
                        <span className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-border text-muted-foreground">
                          {r.category}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-2 font-display text-lg">{r.customer_name}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span className="inline-flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {r.customer_email}
                      </span>
                      {r.preferred_date && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {r.preferred_date}
                        </span>
                      )}
                      {r.budget != null && (
                        <span className="inline-flex items-center gap-1">
                          <Banknote className="h-3 w-3" /> {t("app.pages.requests.budget")}{" "}
                          {Number(r.budget).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">
                      {r.message}
                    </p>
                    {r.quoted_amount != null && (
                      <div className="mt-3 text-sm">
                        {t("app.pages.requests.quotedLabel")}{" "}
                        <span className="font-semibold">
                          {Number(r.quoted_amount).toLocaleString()} {r.currency}
                        </span>
                        {payout != null && (
                          <span className="text-muted-foreground">
                            {" "}
                            → {t("app.pages.requests.yourPayout")} {payout.toFixed(2)} {r.currency}{" "}
                            (
                            {t("app.pages.requests.afterFee", {
                              pct: Math.round(SERVICE_COMMISSION_RATE * 100),
                            })}
                            )
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0">
                    {(r.status === "new" || r.status === "quoted") && (
                      <>
                        <button
                          onClick={() => {
                            setQuoting(quoting === r.id ? null : r.id);
                            setAmount(r.quoted_amount ? String(r.quoted_amount) : "");
                            setNote(r.quoted_note ?? "");
                          }}
                          className="rounded-full brand-gradient text-black px-4 py-1.5 text-xs font-medium"
                        >
                          {r.status === "quoted"
                            ? t("app.pages.requests.updateQuote")
                            : t("app.pages.requests.sendQuote")}
                        </button>
                        <button
                          onClick={async () => {
                            await decline({ data: { id: r.id } });
                            toast.success(t("app.pages.requests.declinedToast"));
                            load();
                          }}
                          className="rounded-full glass border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </>
                    )}
                    {r.status === "paid" && (
                      <button
                        onClick={async () => {
                          await complete({ data: { id: r.id } });
                          toast.success(t("app.pages.requests.completedToast"));
                          load();
                        }}
                        className="rounded-full glass border border-border px-4 py-1.5 text-xs hover:bg-white/5 flex items-center gap-1"
                      >
                        <Check className="h-3 w-3" /> {t("app.pages.requests.markCompleted")}
                      </button>
                    )}
                  </div>
                </div>

                {quoting === r.id && (
                  <div className="mt-4 border-t border-border pt-4 grid sm:grid-cols-[140px_100px_1fr_auto] gap-2">
                    <input
                      type="number"
                      min={1}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={t("app.pages.requests.pricePh")}
                      className="rounded-xl bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
                    />
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value as any)}
                      className="rounded-xl bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none"
                    >
                      {["EUR", "CHF", "USD"].map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={1000}
                      placeholder={t("app.pages.requests.notePh")}
                      className="rounded-xl bg-white/5 border border-border px-3 py-2 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
                    />
                    <button
                      onClick={() => sendQuote(r.id)}
                      disabled={busy}
                      className="rounded-full brand-gradient text-black px-4 py-2 text-xs font-medium disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <Send className="h-3 w-3" />{" "}
                      {busy ? t("app.pages.requests.sending") : t("app.pages.requests.send")}
                    </button>
                    {amount && Number(amount) > 0 && (
                      <div className="sm:col-span-4 text-xs text-muted-foreground">
                        {t("app.pages.requests.customerPays")} {Number(amount).toLocaleString()}{" "}
                        {currency} · {t("app.pages.requests.yourPayout")}{" "}
                        {(Number(amount) * (1 - SERVICE_COMMISSION_RATE)).toFixed(2)} {currency} ·{" "}
                        {t("app.pages.requests.platformFee")}{" "}
                        {(Number(amount) * SERVICE_COMMISSION_RATE).toFixed(2)} {currency}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DashboardShell>
  );
}
